import { formatDate, formatMoney, formatPhone, ListView, PageHeader, useCursorList } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { PAYOUT_STATUS, type PayoutStatus, payoutBadge } from "../../payouts";
import styles from "./payouts.module.css";

const STATUSES = Object.keys(PAYOUT_STATUS) as PayoutStatus[];
/** No status in the URL is the review queue: what needs someone first. */
export const payoutFilters = filterSearch({
  status: [...STATUSES, "all"] as const,
  // one partner's or driver's requests, from their page
  partner_id: "text",
  driver_id: "text",
});

/** "M-Pesa +254 711 220 334" or "Bank 0123456789": where the money goes. */
export const destination = (p: { payout_method: string; payout_destination: string }) =>
  p.payout_method === "mpesa" ? `M-Pesa ${formatPhone(p.payout_destination)}` : `Bank ${p.payout_destination}`;

/** Payouts (P8-17, `Console / Payouts` in the design): partners' and drivers' withdrawals. */
export function PayoutsPage(): ReactNode {
  const search = useSearch({ from: "/console/payouts" });
  const navigate = useNavigate({ from: "/payouts" });
  const format = useCompanyFormat();
  const status = search.status ?? "submitted";
  const list = useCursorList({
    queryKey: ["payouts", status, search.partner_id, search.driver_id],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/wallets/payouts", {
          params: {
            query: {
              status: status === "all" ? undefined : status,
              partner_id: search.partner_id,
              driver_id: search.driver_id,
              cursor,
              limit: 25,
            },
          },
        }),
      ),
  });
  const holder = search.partner_id || search.driver_id ? (list.rows[0]?.requester.name ?? "this requester") : null;
  return (
    <>
      <PageHeader title="Payouts" description="Partners' and drivers' withdrawals: review, approve and pay." />
      {holder ? (
        <p className={styles.muted}>
          Only {holder}'s requests.{" "}
          <Link to="/payouts" search={{ status: search.status }} className={styles.link}>
            Show everyone's
          </Link>
        </p>
      ) : null}
      <ListView
        caption="Payout requests"
        itemName={{ one: "request", other: "requests" }}
        rows={list.rows}
        rowKey={(p) => p.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={
          status === "submitted"
            ? {
                title: "Nothing to review",
                description: "New withdrawal requests from partners and drivers wait here.",
              }
            : status === "approved"
              ? { title: "Nothing to pay", description: "Approved requests wait here until they're paid." }
              : { title: "No requests", description: "Withdrawal requests from partners and drivers show here." }
        }
        toolbar={
          <SegmentedControl
            label="Status"
            value={status}
            onValueChange={(v) =>
              navigate({
                search: (s) => ({ ...s, status: v === "submitted" ? undefined : (v as PayoutStatus | "all") }),
              })
            }
            options={[
              ...STATUSES.map((s) => ({ value: s, label: PAYOUT_STATUS[s][0] })),
              { value: "all", label: "All" },
            ]}
          />
        }
        columns={[
          {
            key: "request_number",
            label: "Request",
            render: (p) => (
              <Link to="/payouts/$payoutId" params={{ payoutId: p.id }} className={styles.number}>
                {p.request_number}
              </Link>
            ),
          },
          {
            key: "requester",
            label: "Requester",
            render: (p) => (
              <span className={styles.stack}>
                <span>{p.requester.name}</span>
                <span className={styles.muted}>{p.requester.kind === "partner" ? "Partner" : "Driver"}</span>
              </span>
            ),
          },
          {
            key: "payout_destination",
            label: "To",
            render: (p) => <span className={styles.muted}>{destination(p)}</span>,
          },
          {
            key: "amount",
            label: "Amount",
            numeric: true,
            render: (p) => formatMoney(p.amount, { ...format, currency: p.currency }),
          },
          { key: "created_at", label: "Asked", render: (p) => formatDate(p.created_at, format) },
          {
            key: "status",
            label: "Status",
            render: (p) => {
              const [label, tone] = payoutBadge(p);
              return <Badge tone={tone}>{label}</Badge>;
            },
          },
        ]}
      />
    </>
  );
}
